export const MAX_TILES_PER_PAGE = 9

/**
 * Tiles keep this shape whatever the window is; the grid is solved for it and
 * whatever space is left over stays empty instead of stretching the tiles.
 */
export const TILE_ASPECT = 16 / 9

export const TILE_GAP = 4

export type GridLayout = {
  columns: number
  rows: number
  tileWidth: number
  tileHeight: number
}

const EMPTY_LAYOUT: GridLayout = { columns: 1, rows: 1, tileWidth: 0, tileHeight: 0 }

export const getGridLayout = (
  count: number,
  width: number,
  height: number,
  aspect: number = TILE_ASPECT,
  gap: number = TILE_GAP
): GridLayout => {
  if (count <= 0 || width <= 0 || height <= 0) {
    return EMPTY_LAYOUT
  }

  let best = EMPTY_LAYOUT

  for (let columns = 1; columns <= count; columns++) {
    const rows = Math.ceil(count / columns)

    const cellWidth = (width - gap * (columns - 1)) / columns
    const cellHeight = (height - gap * (rows - 1)) / rows

    if (cellWidth <= 0 || cellHeight <= 0) {
      continue
    }

    const tileWidth = Math.min(cellWidth, cellHeight * aspect)

    if (tileWidth > best.tileWidth) {
      best = { columns, rows, tileWidth, tileHeight: tileWidth / aspect }
    }
  }

  return best
}

export const getPageWidth = ({ columns, tileWidth }: GridLayout, gap: number = TILE_GAP) =>
  columns * tileWidth + gap * (columns - 1)

export const paginate = <T>(items: T[], perPage: number = MAX_TILES_PER_PAGE): T[][] => {
  if (items.length <= perPage) {
    return [items]
  }

  const pages: T[][] = []

  for (let start = 0; start < items.length; start += perPage) {
    pages.push(items.slice(start, start + perPage))
  }

  return pages
}
